import React from "react";

const InvoiceTemplate = React.forwardRef(({ invoiceData, profile }, ref) => {
  const {
    transaction,
    invoiceNumber,
    invoiceDate,
    amount,
    cgst,
    sgst,
    totalAmount,
  } = invoiceData; 


  /* ================= HELPERS ================= */

  const formatCurrency = (value) => {
    if (value == null || isNaN(value)) return "₹0. 00";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatDateTime = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    return `${d.toLocaleDateString("en-GB")} | ${d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })}`;
  };

  /* ================= SAFE DATA MAPPING ================= */

  // The profile object from useProfileStore is now flattened, but may still contain a .builder sub-object
  const profileData = profile || {};
  const builder = profileData.builder || profileData;

  const displayName =
    builder.company_name ||
    builder.engineer_name ||
    "N/A";

  const ducktailId =
    builder.ducktail_id ||     // From new flattened structure or backend field name
    builder.builder_id ||      // Fallback
    "N/A";

  const gstNumber =
    builder.gst_number || "N/A";

  const email =
    builder.email ||
    profileData.email ||
    "N/A";

  const phone =
    builder.contact_number ||
    builder.user_account_phone ||
    builder.whatsapp_number ||
    "N/A";

  /* ================= JSX ================= */

  return (
    <div
      ref={ref}
      className="bg-white max-w-5xl mx-auto p-10 text-gray-900"
      style={{ fontFamily: "Inter, Arial, sans-serif" }}
    >
      {/* ================= HEADER ================= */}
      <div className="flex justify-between items-start border-b pb-6 mb-10">
        {/* LOGO LEFT */}
        <div className="w-1/2">
          <img
            src="/assets/black logo.png"
            alt="Ducktail Logo"
            className="h-20 object-contain"
          />
        </div>

        {/* INVOICE META RIGHT */}
        <div className="w-1/2 text-right text-sm">
          <p className="text-xl font-extrabold tracking-tight uppercase">
            Tax Invoice
          </p>
          <p className="text-gray-600 mt-1">
            Invoice No: <span className="font-medium">{invoiceNumber}</span>
          </p>
          <p className="text-gray-600 font-medium">
            {formatDateTime(invoiceDate)}
          </p>
        </div>
      </div>

      {/* ================= BILL TO / COMPANY ================= */}
      <div className="grid grid-cols-2 gap-8 mb-12">
        {/* BILL TO */}
        <div className="bg-gray-50 p-5 rounded-xl">
          <h3 className="text-xs font-bold uppercase text-gray-500 mb-3">
            Billed To
          </h3>
          <p className="font-bold text-lg mb-2">{displayName}</p>

          <p className="text-sm">
            Ducktail ID: <span className="font-medium">{ducktailId}</span>
          </p>

          <p className="text-sm">
            GST: <span className="font-medium">{gstNumber}</span>
          </p>

          <p className="text-sm">
            Email: <span className="font-medium">{email}</span>
          </p>

          <p className="text-sm">
            Phone: <span className="font-medium">{phone}</span>
          </p>
        </div>

        {/* COMPANY DETAILS */}
        <div className="bg-gray-50 p-5 rounded-xl">
          <h3 className="text-xs font-bold uppercase text-gray-500 mb-3">
            Company Details
          </h3>
          <p className="font-bold text-lg">Ducktail Private Limited</p>
          <p className="text-sm">Tamilnadu - 627105</p>
        </div>
      </div>

      {/* ================= GST TABLE ================= */}
      <div className="mb-12">
        <table className="w-full border-collapse border border-gray-300">
          <thead className="bg-gray-50 text-xs uppercase text-gray-700">
            <tr className="divide-x border-b font-black">
              <th className="py-3 px-2 text-center w-16">S.No</th>
              <th className="py-3 px-4 text-left">Description</th>
              <th className="py-3 px-4 text-right w-32">Price</th>
              <th className="py-3 px-4 text-right w-32">CGST (9%)</th>
              <th className="py-3 px-4 text-right w-32">SGST (9%)</th>
              <th className="py-3 px-4 text-right w-32">Total</th>
            </tr>
          </thead>

          <tbody className="text-sm divide-y">
            <tr className="divide-x">
              <td className="py-6 px-2 text-center">1</td>

              <td className="py-6 px-4">
                <p className="font-bold text-base">
                  {transaction?.type === "RENEWAL"
                    ? "Subscription Renewal"
                    : transaction?.type === "EXPANSION"
                      ? "Service Area Expansion"
                      : "Subscription Plan"}
                </p>
                <p className="text-xs text-gray-500 italic">
                  {transaction?.duration_months || 12} months subscription
                </p>
              </td>

              <td className="py-6 px-4 text-right font-medium">
                {formatCurrency(amount)}
              </td>

              <td className="py-6 px-4 text-right">
                {formatCurrency(cgst)}
              </td>

              <td className="py-6 px-4 text-right">
                {formatCurrency(sgst)}
              </td>

              <td className="py-6 px-4 text-right font-black text-base">
                {formatCurrency(totalAmount)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ================= TERMS ================= */}
      <div className="text-xs text-gray-600 border-t pt-6 space-y-1">
        <p>• This is a computer-generated GST invoice.</p>
        <p>• Payment is non-refundable as per subscription policy.</p>
        <p>
          • Subscription valid for {transaction?.duration_months || 12} months
          from activation date.
        </p>
      </div>

      {/* ================= FOOTER ================= */}
      <div className="text-center mt-8">
        <p className="text-sm font-medium">Thank you for your business</p>
        <p className="text-xs text-gray-500">
          DUCKTAIL | The Construction Platform
        </p>
      </div>
    </div>
  );
});

InvoiceTemplate.displayName = "InvoiceTemplate";
export default InvoiceTemplate;
